/**
 * Utilitários para detecção, extração de ID e integração com a API do YouTube
 */

const YOUTUBE_REGEX = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/i;
const DIRECT_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extrai o ID do vídeo do YouTube a partir de qualquer formato de URL ou ID direto.
 */
export function extractYouTubeId(urlOrId?: string | null): string | null {
  if (!urlOrId) return null;
  const trimmed = urlOrId.trim();
  if (!trimmed) return null;

  if (DIRECT_ID_REGEX.test(trimmed)) {
    return trimmed;
  }

  const match = trimmed.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
}

/**
 * Verifica se a string fornecida é um link ou ID válido do YouTube.
 */
export function isYouTubeUrl(urlOrId?: string | null): boolean {
  return extractYouTubeId(urlOrId) !== null;
}

/**
 * Retorna a URL da imagem de capa/thumbnail do vídeo do YouTube em formato moderno WebP de alta compactação.
 */
export function getYouTubeThumbnail(urlOrId?: string | null, quality: 'max' | 'hq' | 'mq' | 'default' = 'mq', preferWebp: boolean = true): string {
  const videoId = extractYouTubeId(urlOrId);
  if (!videoId) return '';
  
  if (preferWebp) {
    switch (quality) {
      case 'max':
        return `https://img.youtube.com/vi_webp/${videoId}/maxresdefault.webp`;
      case 'hq':
        return `https://img.youtube.com/vi_webp/${videoId}/hqdefault.webp`;
      case 'mq':
      default:
        // 320x180 WebP ideal para mobile e cards (apenas ~8-12KB contra 36KB de JPEG)
        return `https://img.youtube.com/vi_webp/${videoId}/mqdefault.webp`;
    }
  }

  switch (quality) {
    case 'max':
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    case 'hq':
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    case 'mq':
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    default:
      return `https://img.youtube.com/vi/${videoId}/default.jpg`;
  }
}

// Promise única para carregar o script do YouTube Iframe API apenas uma vez
let ytApiPromise: Promise<void> | null = null;

/**
 * Carrega dinamicamente a biblioteca oficial YouTube IFrame Player API.
 */
export function loadYouTubeIFrameAPI(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window not defined'));

  // Se já está carregado
  if ((window as any).YT && (window as any).YT.Player) {
    return Promise.resolve();
  }

  if (ytApiPromise) {
    return ytApiPromise;
  }

  ytApiPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById('youtube-iframe-api-script');
    
    const prevCallback = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      if (typeof prevCallback === 'function') {
        try { prevCallback(); } catch (_) {}
      }
      resolve();
    };

    if (!existingScript) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api-script';
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      tag.onerror = () => {
        ytApiPromise = null;
        reject(new Error('Falha ao carregar a API do YouTube'));
      };
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(tag, firstScript);
      } else {
        document.head.appendChild(tag);
      }
    }
  });

  return ytApiPromise;
}
